import { unwrap } from "@lvt/web-data";
import { Box } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { fetchStreamMetadata, getHeater, getLights } from "@/api/streamMetadata";
import InformationalVideoModalWrapper from "@/common/components/InformationalVideoModal/InformationalVideoModalWrapper";
import { Page } from "@/common/components/InformationalVideoModal/types";
import StreamContent from "@/common/components/streaming/StreamContent/StreamContent";
import { RootState, useSelector, useDispatch } from "@/store";
import { setStreamingCamera } from "@/store/cameras/slice";
import { useFullStory } from "@/utilities/hooks/fullstory";
import { storeRecentCamera } from "@/utilities/localStorage/recentStreamingCameras";

import useStyles from "./StreamPage.styles";


export interface StreamPageParams {
    cameraId: string;
}

export const StreamPage: React.FC = () => {
    const { cameraId: cameraIdString } = useParams<StreamPageParams>();
    const cameraId = parseInt(cameraIdString);
    const dispatch = useDispatch();
    const FullStory = useFullStory();
    const meta = useSelector((state) => unwrap(state.stream.streamMeta));
    const userPermissions = useSelector((state) => state.user.permissions);
    const classes = useStyles();

    const { alerts } = useSelector((state: RootState) => state.alerts);

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        alerts.forEach((alert) => {
            const AlertLanding = (
                <Alert key={alert.index} severity={alert.severity}>
                    {alert.message}
                </Alert>
            );

            enqueueSnackbar(AlertLanding, {
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
                autoHideDuration: 8000,
                key: alert.message,
                persist: false,
                preventDuplicate: true,
                variant: alert.severity,
            });
        });
    }, [alerts]);

    useEffect(() => {
        dispatch(setStreamingCamera(cameraId));
        dispatch(fetchStreamMetadata(cameraId));
    }, [cameraId]);

    useEffect(() => {
        if (meta) {
            storeRecentCamera(meta.liveUnitId, meta.cameraId);
            FullStory.event(FullStory.names.events.streamPageLoaded, { live_unit_id: meta.liveUnitId });
        }
    }, [meta]);

    const canStreamIfDeactivated = (
        (meta && userPermissions) ?
            meta.cameraActive ?
                true
                :
                userPermissions.streamDeactivated
            :
            true // we don't have the meta yet, so don't worry about it until we have it.
    );

    return (
        <Box className={classes.root}>
            <InformationalVideoModalWrapper page={Page.VmsStream} />
            {canStreamIfDeactivated &&
                <StreamContent
                    liveUnitId={meta?.liveUnitId}
                    cameraId={cameraId}
                    allowControlPresets={!!meta?.permissions.controlPresets && (!!meta?.capabilities.presets || !!meta?.capabilities.position)}
                    allowPanTilt={!!meta?.permissions.controlPtzf && (!!meta?.capabilities.pan)}
                    allowClickToCenter={!!meta?.permissions.controlPtzf && (!!meta?.capabilities.pan && !!meta?.capabilities.clickToCenter)}
                    allowZoom={!!meta?.permissions.controlPtzf && (!!meta?.capabilities.pan && !!meta?.capabilities.zoom)}
                    allowSpeakerTalkdown={!!meta?.capabilities.speakerTalkdown && !!meta?.permissions.speakerTalkdown}
                    allowQuickSounds={!!meta?.capabilities.speakerSounds && !!meta?.permissions.quickSounds}
                    allowLights={!!meta?.permissions.controlPtzf && !!getLights(meta.capabilities.relayActions).length}
                    allowSnapshot={!!meta?.capabilities.snapshot && !!meta?.permissions.controlPtzf}
                    allowHeater={!!meta?.capabilities.heater && !!getHeater(meta.capabilities.relayActions)}
                    allowSuspendPatol={!!meta?.permissions.suspendPatrol}
                    allowSelfService={!!userPermissions?.selfService}
                    clientLogo={meta?.clientLogoUrl ? meta.clientLogoUrl : undefined}
                />
            }
        </Box>
    );
};
